import React, { useEffect, useState } from "react"
import { AutoSizer } from "react-virtualized"
import Plot from "react-plotly.js"
import {
  PieChart,
  Pie,
  Cell,
  XAxis,
  Tooltip,
  ResponsiveContainer,
  RadarChart,
  PolarGrid,
  PolarRadiusAxis,
  PolarAngleAxis,
  Radar,
  Legend,
  Line,
} from "recharts"
import { CustomTooltip, RenderActiveShape } from "../Tooltip"
import { BarChartVariation, BarGroup, BarPercent } from "../BarChart"
import { formatToDDMMYYYY, TableVirtualized } from "../../components/Table/index"
import { Card, CardTitle } from "../Card"
import { EmptyGraph } from "../Empty"
import SweetAlert from 'sweetalert';

import "./styles.scss"
import Send from "../../core/communication"
import { Spinner } from "../Loading"
import { useMediaQuery } from "react-responsive"

const COLORS = [
  "#FF8C02", // secondary-orange
  "#FF4001", // primary-orange
  "#004001", // primary-green
  "#0146ad", // tertiary-green
  "#465B4C", // quaternary-green/primary-green-dark-medium
  "#687B71", // moss-primary-green
  "#D31104", // red-blood/warning
  "#001C00", // secondary-green
  "#A8B1AC", // gray-dark-medium
  "#87938A", // gray-dark-medium-two
  "#4F604F", // text-color-medium
  "#99AA99", // gray-dark-medium-three
  "#D5DCD8", // gray-light-medium
  "#FF6B33", // variação orange clara
  "#FF9E66", // variação orange mais clara
  "#1A5A1A", // variação green dark
  "#2E6B3E", // variação green medium
  "#5A7A5A", // variação green gray
  "#7A997A", // variação green light
  "#33663E", // variação moss dark
  "#9CB19C", // variação moss light
  "#B34D40", // variação red orange
  "#8B1E0F", // variação red dark
  "#E84A33", // variação red light
  "#5C6F5C", // variação gray green dark
  "#B8C5B8", // variação gray green light
  "#708470", // variação text medium
  "#3D4D3D", // variação text dark
  "#636E63", // variação text light
  "#1F2F1F"  // variação very dark green
]

// Função para gerar cor consistente baseada no label
const getColorForLabel = (label, uniqueLabels, colors = COLORS) => {
  if (!uniqueLabels || !Array.isArray(uniqueLabels)) {
    return colors[0] || "#FF8C02"
  }
  
  const index = uniqueLabels.indexOf(label)
  return index >= 0 ? colors[index % colors.length] : colors[0]
}

// Função específica para unificar dados de tabelas por produtor
const unifyTableDataByProducer = (data) => {
  if (!data || !data.fields || !Array.isArray(data.fields)) {
    return data
  }

  console.log('🔍 UNIFY TABLE DEBUG - Iniciando unificação')
  console.log('Campos originais:', data.fields.length)
  console.log('Exemplo de campo:', data.fields[0])

  const unifiedFields = {}
  
  data.fields.forEach((field, index) => {
    const producerId = field.ProdutorId || field.id
    const producerName = field.Produtor || field.produtor
    const dateField = field.Inicio || field.inicio || field.data || field.Data
    
    console.log(`Campo ${index}:`, {
      ID: producerId,
      Produtor: producerName,
      Data: dateField
    })
    
    if (producerId && producerName) {
      const key = `${producerId}_${producerName}`
      console.log(`Chave gerada: ${key}`)
      
      if (!unifiedFields[key]) {
        console.log('✅ Primeira ocorrência - adicionando')
        unifiedFields[key] = { ...field }
      } else {
        console.log('🔄 Já existe - combinando dados')
        const existing = unifiedFields[key]
        const existingDate = new Date(existing.Inicio || existing.inicio || existing.data || existing.Data || '1900-01-01')
        const currentDate = new Date(dateField || '1900-01-01')
        
        // Para cada campo, usar o valor mais recente que não seja vazio/null/undefined
        Object.keys(field).forEach(columnKey => {
          const currentValue = field[columnKey]
          const existingValue = existing[columnKey]
          
          // Se o valor atual não está vazio e (não existe valor anterior OU o atual é mais recente)
          if (currentValue !== null && currentValue !== undefined && currentValue !== '' && 
              (existingValue === null || existingValue === undefined || existingValue === '' || currentDate >= existingDate)) {
            existing[columnKey] = currentValue
          }
        })
        
        // Sempre manter a data mais recente
        if (currentDate > existingDate) {
          existing.Inicio = field.Inicio || existing.Inicio
          existing.inicio = field.inicio || existing.inicio
          existing.data = field.data || existing.data
          existing.Data = field.Data || existing.Data
        }
      }
    } else {
      console.log('⚠️ Sem identificador de produtor - adicionando diretamente')
      const randomKey = `${Date.now()}_${Math.random()}`
      unifiedFields[randomKey] = field
    }
  })
  
  // Converter de volta para array
  const unifiedFieldsArray = Object.values(unifiedFields)
  
  console.log('🎯 RESULTADO DA UNIFICAÇÃO:')
  console.log('Campos finais:', unifiedFieldsArray.length)
  console.log('Chaves geradas:', Object.keys(unifiedFields))
  console.log('Exemplo de campo unificado:', unifiedFieldsArray[0])
  
  return {
    ...data,
    fields: unifiedFieldsArray
  }
}

function capitalize(str) {
  if (typeof str !== 'string') return '';

  return str.charAt(0).toUpperCase() + str.slice(1).toLowerCase();
}

export const componentType = (type, props) => {
  const {
    period,
    periodReference,
    keyWord,
    data,
    showEmptyRef,
    isRef,
  } = props;

  const hasData = data?.fields?.length > 0;
  const shouldShowReferenceGraph = !isRef || (period && periodReference);

  const title = isRef
    ? period && periodReference
      ? `(${period}) ${keyWord}`
      : keyWord
    : period
      ? `(${period}) ${keyWord}`
      : keyWord;

  return (
    <Card className="card-dynamic-dash-card" style={{ width: "100%" }}>
      <CardTitle title={title} />

      {shouldShowReferenceGraph && (hasData && type !== "empty") ? (
        <>
          {type === "bar" && <BarChartVariation {...props} />}
          {type === "bar-variation" && <BarChartVariation {...props} hasVariation />}
          {type === "bar-percent" && <BarPercent {...props} />}
          {type === "bar-group" && <BarGroup {...props} />}
          {type === "radar" && <RadarRecharts {...props} />}
          {type === "donuts" && <PieRecharts {...props} isDonut />}
          {type === "pie" && <PieRecharts {...props} />}
          {type === "table" && <TableVirtualized {...{...props, data: props.data, distinct: props.distinct}} hasBackground />}
          {type === "cardInfo" && <CardInfo {...props} />}
        </>
      ) : (
        <EmptyGraph />
      )}
    </Card>
  );
};

export const PiePlotChart = ({ title, data, onFilter }) => {
  return (
    <AutoSizer>
      {({ height, width }) => {
        return (
          <Plot
            data={[
              {
                values: data.map((item) => item.value),
                labels: data.map((item) => item.label),
                marker: { colors: ["aliceblue", "aqua", "aquamarine", "darkturquoise"] },
                type: "pie",
              },
            ]}
            layout={{
              height,
              width,
              title,
              showlegend: false,
            }}
            config={{
              locale: "pt-br",
              displaylogo: false,
            }}
            onClick={({ points }) => {
              const label = points.map((item) => item.label).join("")
              // onFilter(label)
            }}
          />
        )
      }}
    </AutoSizer>
  )
}

///////// React Charts

export const PieRecharts = ({ data, isDonut, showOnlyName = false, outerRadiusWidth, width, height, uniqueLabels, syncColors, ...props }) => {
  const [formattedData, setFormattedData] = useState([])
  const [activeIndex, setActiveIndex] = useState(0)
  const [loading, setLoading] = useState()

  const { noLabels } = props;

  useEffect(() => {
    if (data) {
      let newData = [];

      data?.fields
        ?.filter((item) => item.inFilter)
        .forEach((item) => {
          const answer = item[data?.value] ? `${item[data?.value]}`.split('|') : [];
          const normalizedAnswersSet = new Set(answer.map(row => capitalize(row.trim())));

          normalizedAnswersSet.forEach((row) => {
            const isTruthy = row === true || row?.toLowerCase() === 'true';
            const isFalsy = row === false || row?.toLowerCase() === 'false';

            const value = isTruthy ? 'Sim' : isFalsy ? 'Não' : `${row}`;

            const findIndex = newData.findIndex((item) => item.value === value);

            if (findIndex !== -1) {
              const existingItem = newData[findIndex];

              if (!existingItem.producersSet) {
                existingItem.producersSet = new Set(existingItem.producers.split('|'));
              }

              existingItem.producersSet.add(item["Produtor"]);

              existingItem.producers = Array.from(existingItem.producersSet).join('|');

              existingItem.quantity = existingItem.producersSet.size;
            } else {
              newData.push({
                name: data?.title ?? '',
                producers: item["Produtor"],
                producersSet: new Set([item["Produtor"]]),
                value,
                quantity: 1,
              });
            }
          });
        });

      newData = newData.map(({ producersSet, ...rest }) => rest);

      setFormattedData(newData);
    }

    return () => setFormattedData([]);
  }, [data]);

  const alerts = data?.alerts || [];

  const CustomXAxisTick = ({ x, y, payload }) => {
    if (payload && payload.value) {
      return (
        <Text
          fontSize={"12px"}
          width={"12px"}
          x={x}
          y={y}
          textAnchor="middle"
          verticalAnchor="start">
          {payload.value}
        </Text>
      )
    }
    return null
  }

  const existsFields = data?.fields?.length > 0;

  const formatChart = existsFields ? formattedData : data

  const renderActiveShape = <RenderActiveShape showOnlyName={showOnlyName} noLabels={noLabels} />

  const hasAlert = formatChart.find(item => alerts.find(alert => alert.option === item.value))

  const showUserAlert = (hasAlert) => {
    SweetAlert({
      title: 'Produtores que reportaram',
      icon: "error",
      text: `${hasAlert.producers}`.split("|").join("\n\n"),
      type: 'warning',
      confirmButtonText: 'Fechar',
      html: true,
    });
  };

  const showSuggest = async (hasAlert) => {
    setLoading(true)
    try {
      const data = await Send.post("/ia/recommend-action", {
        text: `Meu indicador está com problema. Me diga ações para tentar resolver
          Indicador: ${hasAlert.name}
          Valor: ${hasAlert.value}`,
      }).then(data => data.result);

      console.log()

      if (data && data.length > 0) {
        SweetAlert({
          title: 'Ações recomendadas',
          icon: "success",
          text: data.map(item => `${item.action}`).join("\n\n"),
          type: 'success',
          html: true,
          confirmButtonText: 'Fechar'
        });
      }
    } catch (e) {
      console.log({ e });
    }

    setLoading(false)
  }

  return (
    <>
      {hasAlert && <div className="alert-suggest">
        <div className="alert-container-div" onClick={() => showUserAlert(hasAlert)}><div>⚠</div><b className="text-danger">Indicador em estado de alerta</b></div>
        <div className="alert-container-div warning" onClick={() => showSuggest(hasAlert)}><div>{loading ? <Spinner color="#FF8C02" /> : "?"}</div><b>Mostrar sugestões</b></div>
      </div>}
      <ResponsiveContainer width="100%" height="100%">
        <PieChart width={600} height={600} data={formatChart}>
          <Pie
            data={formatChart}
            cx="50%"
            cy={180}
            dataKey={existsFields ? "quantity" : "value"}
            nameKey={"name"}
            outerRadius={outerRadiusWidth || 115}
            innerRadius={isDonut ? 40 : 0}
            activeIndex={activeIndex}
            onMouseEnter={(_, index) => setActiveIndex(index)}
            label={props.showAllLabels ? renderActiveShape : undefined}
            activeShape={!props.showAllLabels ? renderActiveShape : undefined}>

            <XAxis
              interval={0}
              tick={<CustomXAxisTick />}
              dataKey={existsFields ? "quantity" : "value"}
            />
            {formatChart.map((entry, index) => {
              const alert = alerts.find(alert => alert.option === entry.value)
              
              // Para gráficos de pizza, sempre usar cores diferentes para cada fatia baseadas no index
              // A sincronização não se aplica a gráficos de pizza da mesma forma que barras
              const color = COLORS[index % COLORS.length]
              
              return (
                <Cell key={`cell-${index}`} fill={alert ? alert.color || "#d0342c" : color} />
              )
            })}
          </Pie>
        </PieChart>
      </ResponsiveContainer>
    </>
  )
}

export const PieDonutsRecharts = ({ data }) => {
  const [formattedData, setFormattedData] = useState([])

  useEffect(() => {
    if (data) {
      let newData = []
      data?.fields &&
        data.fields.map((item) => {
          if (
            newData &&
            newData.map((field) => field?.valueName).indexOf(item["resposta"]) !== -1
          ) {
            const dataPosition = newData.map((field) => field?.valueName).indexOf(item["resposta"])
            ++newData[dataPosition].value
            newData[dataPosition].name = newData[dataPosition].name.concat(`|${"Produtor"}`)
            return
          }

          newData.push({
            valueName: item["resposta"],
            value: 1,
            name: "Produtor",
          })
        })

      setFormattedData(newData)
    }
  }, data)

  return (
    <ResponsiveContainer width="100%" height="100%">
      <PieChart width={500 || width} height={500 || height}>
        <Pie
          dataKey="value"
          data={formattedData}
          cx="50%"
          cy="50%"
          innerRadius={40}
          outerRadius={80}
          fill="#82ca9d">
          {formattedData.map((_, index) => (
            <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
          ))}
        </Pie>
        <Tooltip />
      </PieChart>
    </ResponsiveContainer>
  )
}

export const PieDonutsDistActivities = ({ data, handleClick }) => {
  const [formattedData, setFormattedData] = useState([]);
  const [clickedIndex, setClickedIndex] = useState(null);

  const COLORS = ['#00A800', '#FFC103', '#FF8202', '#4992D4'];

  const hexToRgba = (hex, opacity) => {
    const strippedHex = hex.replace('#', '');
    const r = parseInt(strippedHex.substring(0, 2), 16);
    const g = parseInt(strippedHex.substring(2, 4), 16);
    const b = parseInt(strippedHex.substring(4, 6), 16);

    return `rgba(${r}, ${g}, ${b}, ${opacity})`;
  };

  const colorsWithOpacity = COLORS.map(color => hexToRgba(color, 0.5));

  useEffect(() => {
    if (data) {
      const total =
        (+data.assistance?.completed || 0) +
        (+data.actionPlan?.completed || 0) +
        (+data.indicators?.completed || 0) +
        (+data.complementarForms?.completed || 0);

      const newData = [
        { name: 'Atendimento', value: +data.assistance?.completed || 0, percent: ((+data.assistance?.completed || 0) / total) * 100 },
        { name: 'Plano de Ação', value: +data.actionPlan?.completed || 0, percent: ((+data.actionPlan?.completed || 0) / total) * 100 },
        { name: 'Formulário Complementar', value: +data.complementarForms?.completed || 0, percent: ((+data.complementarForms?.completed || 0) / total) * 100 },
        { name: 'Indicador', value: +data.indicators?.completed || 0, percent: ((+data.indicators?.completed || 0) / total) * 100 }
      ];

      setFormattedData(newData);
    }
  }, [data]);

  const handleClicked = (e, i) => {
    if (i === clickedIndex) {
      setClickedIndex(null);
      typeof handleClick === "function" && handleClick({}, null);
    } else {
      setClickedIndex(i);
      typeof handleClick === "function" && handleClick(e, i);
    }
  };

  const HD = useMediaQuery({ query: "(min-width:1280px) and (max-width:1366px)" });

  const width = HD ? 220 : 300;
  const height = HD ? 220 : 300;
  const innerRadius = HD ? 40 : 80;

  return (
    <ResponsiveContainer width="100%" height="100%">
      {formattedData.some(v => v.value !== 0) ? (
        <PieChart width={width} height={height}>
          <Pie
            data={formattedData}
            cx="50%"
            cy="50%"
            labelLine={false}
            innerRadius={innerRadius}
            fill="#F2F2F2"
            dataKey="value"
            onClick={handleClicked}
          >
            {formattedData.map((entry, index) => (
              <Cell
                key={`cell-${index}`}
                fill={
                  clickedIndex === index
                    ? colorsWithOpacity[index % colorsWithOpacity.length]
                    : COLORS[index % COLORS.length]
                }
              />
            ))}
          </Pie>
          <Tooltip
            formatter={(value, name, props) => [`${props?.payload?.percent?.toFixed(2) || 0}%`, name]}
          />
        </PieChart>
      ) : (
        <div className="chart-pie-empty-data"><h3>Sem dados</h3></div>
      )}
    </ResponsiveContainer>
  );
};

export const RadarRecharts = ({ data, uniqueLabels, syncColors }) => {
  // const [maxProducer, setMaxProducer] = useState(0)

  // useEffect(() => {
  //   if (data) {
  //     setMaxProducer(data?.fields.length || 0)
  //   }
  // }, [data])

  const CustomTick = ({ payload, x, y, textAnchor, stroke, radius, ...data }) => {
    console.log({ payload, x, y, textAnchor, stroke, radius, data });
    return (
      <text
        radius={radius}
        stroke={stroke}
        fontSize={"12px"}
        x={x}
        y={y}
        className="recharts-text recharts-polar-angle-axis-tick-value"
        text-anchor={textAnchor}>
        <tspan x={x} dy="1em">
          {payload.value}
        </tspan>
      </text>
    )
  }

  return (
    <ResponsiveContainer width="100%" height="100%">
      <RadarChart
        cx="50%"
        cy="50%"
        outerRadius="80%"
        data={data?.fields?.length ? data.fields : data}>
        <PolarGrid />
        <PolarAngleAxis tickFormatter={(index) => {
          return data.fields[index]?.name
        }} />
        <PolarRadiusAxis />
        <Radar
          name="Período Anterior"
          dataKey="reference"
          stroke={syncColors && uniqueLabels ? getColorForLabel("Período Anterior", uniqueLabels, COLORS) : "#8884d8"} 
          fill={syncColors && uniqueLabels ? getColorForLabel("Período Anterior", uniqueLabels, COLORS) : "#8884d8"}
          fillOpacity={0.6}
        />
        <Radar
          name="Período Atual"
          dataKey="value"
          stroke={syncColors && uniqueLabels ? getColorForLabel("Período Atual", uniqueLabels, COLORS) : "#82ca9d"} 
          fill={syncColors && uniqueLabels ? getColorForLabel("Período Atual", uniqueLabels, COLORS) : "#82ca9d"}
          fillOpacity={0.6}
        />
        <Legend />
        <Tooltip content={<CustomTooltip />} cursor={{ fill: "transparent" }} />
      </RadarChart>
    </ResponsiveContainer>
  )
}

//////////// Cards

const calculateAge = (birthDate) => {
  let birth;

  if (typeof birthDate === 'string') {
    // Verifica se é no formato DD/MM/YYYY
    if (/^\d{2}\/\d{2}\/\d{4}$/.test(birthDate)) {
      const [day, month, year] = birthDate.split('/');
      birth = new Date(`${year}-${month}-${day}`);
    }
    // Verifica se é no formato YYYY-MM-DD
    else if (/^\d{4}-\d{2}-\d{2}$/.test(birthDate)) {
      birth = new Date(birthDate);
    }
  }

  if (birthDate instanceof Date) {
    birth = birthDate;
  }

  if (!birth || isNaN(birth.getTime())) {
    return null;
  }

  const today = new Date();
  let age = today.getFullYear() - birth.getFullYear();
  const currentMonth = today.getMonth();
  const birthMonth = birth.getMonth();

  if (
    currentMonth < birthMonth ||
    (currentMonth === birthMonth && today.getDate() < birth.getDate())
  ) {
    age--;
  }

  return age;
};

export const CardInfo = React.memo(({ data }) => {
  const [formattedData, setFormattedData] = useState([]);

  const getGroupFormat = (fields = [], codes = [], groupType = "média") => {
    let totalSum = 0;
    let totalSumToDivide = 0
    let sumToDividePos0 = 0
    let sumToDividePos1 = 0
    let totalLength = fields?.length || 1;
    let idades = [];

    fields.forEach((item, index) => {
      let values = codes.map(c => {
        if (c?.includes("NASCIMENTO") && item[c]) {
          return calculateAge(item[c]);
        }
        return parseFloat(item[c]) || 0;
      });

      let sumValues = values.reduce((sum, val) => sum + val, 0);
      let avgValues = values.length > 0 ? sumValues / values.length : 0;

      idades.push(...values.filter(v => v > 0));

      switch (groupType) {
        case "media":
        case "média":
          totalSum += avgValues;
          break;
        case "soma":
          totalSum += sumValues;
          break;
        case "somaanual":
          totalSum += sumValues * 12;
          break;
        case "produtividade":
          totalSum = values[0] / values[1];
          break;
        case "divide":
        case "divisao":
        case "divisão":
          totalSum = (sumToDividePos0 += values[0]) / (sumToDividePos1 += values[1]);
          break;
        case "máximo":
        case "maximo":
          totalSum = index === 0 ? Math.max(...values) : Math.max(totalSum, ...values);
          break;
        case "mínimo":
        case "minimo":
          totalSum = index === 0 ? Math.min(...values) : Math.min(totalSum, ...values);
          break;
        case "último":
        case "ultimo":
          if (fields.length - 1 === index) totalSum = sumValues;
          break;
        case "primeiro":
          if (index === 0) totalSum = sumValues;
          break;
      }
    });

    if (groupType === "mínimo" || groupType === "minimo") {
      totalSum = Math.min(...idades);
    }

    if (groupType === "máximo" || groupType === "maximo") {
      totalSum = Math.max(...idades);
    }

    return { totalSum, totalLength };
  };

  useEffect(() => {
    if (!data) return;

    const cardsDetails = Array.isArray(data?.codes) ? data.codes : [];

    const newData = cardsDetails.map((detail) => {
      let codesArray = [];

      if (Array.isArray(detail.codes) && typeof detail.codes[0] === "string") {
        codesArray = detail.codes;
      } else if (Array.isArray(detail.codes)) {
        codesArray = detail.codes.map(c => c.code).filter(Boolean);
      } else if (detail.code) {
        codesArray = [detail.code];
      }

      const fields = data?.fields?.filter(item => codesArray.some(c => item[c] !== undefined)) || [];
      const groupType = detail.groupBy?.toLowerCase().split("-")[0];
      const format = detail.groupBy?.toLowerCase().split("-")[1];

      const { totalSum, totalLength } = getGroupFormat(fields, codesArray, groupType);

      let value = 0;
      let media = totalSum / totalLength;

      if (groupType === "média" || groupType === "media") {
        value = parseFloat(`${media.toFixed(2)}`).toLocaleString("pt-BR");
      } else {
        value = parseFloat(`${totalSum.toFixed(2)}`).toLocaleString("pt-BR");
      }

      if (format && format === "renda") {
        value = value.toLocaleString("pt-BR", { style: "currency", currency: "BRL" });
      }

      return { title: detail?.title, value };
    });

    setFormattedData(newData);
  }, [data]);

  return (
    <div className="card-info-container">
      {formattedData?.length > 0 ? (
        formattedData.map((data, index) => (
          <React.Fragment key={index}>
            <div className="card-info-header">
              <div className="card-info-title">{data?.title || "Sem título"}</div>
            </div>
            <div className="card-info-value">{data?.value || 0}</div>
          </React.Fragment>
        ))
      ) : (
        <p>Nenhum dado disponível</p>
      )}
    </div>
  );
});



